import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Apartment } from '../../../../../graphql/graphql';
import { InfoBlock, InfoContainer, InfoItem } from './styled';
import { BottomPhoneButton } from '../../../BottomPhoneButton';
import { PhoneButtonClickHandler } from '../../../BottomPhoneButton/types';
import { Typography } from '../../../../ui/Typography';
import { prettyNumber } from '../../../../../utils/helpers';

interface ApartmentInfoBlockProps {
  apartment: Apartment;
  phones: string[] | null;
  isVisible: boolean;
  onClickHandler: PhoneButtonClickHandler;
  setIsVisible: (value: boolean) => void;
}

export const ApartmentInfoBlock: React.FC<ApartmentInfoBlockProps> = ({
  apartment,
  phones,
  isVisible,
  onClickHandler,
  setIsVisible
}) => {
  const { t } = useTranslation();

  const {
    rooms,
    area,
    priceFrom,
    currency,
    repair,
    bathroomsCount,
    balconiesCount
  } = apartment;

  const totalPrice = prettyNumber(Math.round(area * priceFrom));
  const pricePerMeter = prettyNumber(priceFrom);

  const info = useMemo(
    () => [
      {
        title: t('residences.price'),
        value: t('apartment_price', { price: totalPrice, currency })
      },
      {
        title: t('residences.price_per_unit'),
        value: t('apartment_price_per_meter', {
          price: pricePerMeter,
          currency
        })
      },
      {
        title: t('apartment_repair'),
        value: repair?.name,
        isShown: !!repair?.name
      },
      {
        title: t('apartment_bathroom_count'),
        value: bathroomsCount,
        isShown: bathroomsCount !== null && bathroomsCount !== undefined
      },
      {
        title: t('apartment_balcon_count'),
        value: balconiesCount,
        isShown: balconiesCount !== null && balconiesCount !== undefined
      }
    ],
    [
      t,
      repair?.name,
      balconiesCount,
      bathroomsCount,
      totalPrice,
      currency,
      pricePerMeter
    ]
  );

  return (
    <InfoBlock>
      <Typography tag="h4_new">
        <span>
          {t('rooms.rooms_count', {
            count: rooms,
            postProcess: 'interval'
          })}
        </span>
        <span>{t('area_metres', { area })}</span>
      </Typography>

      <InfoContainer>
        {info.map(({ title, value, isShown = true }) => {
          if (!isShown) return null;
          return (
            <InfoItem key={title}>
              <Typography tag="body_1">{title}</Typography>
              <Typography tag="body_1">{value}</Typography>
            </InfoItem>
          );
        })}
      </InfoContainer>

      <BottomPhoneButton
        stat="residence-apartment-phones-btn"
        phones={phones}
        clickHandler={onClickHandler}
        {...{ setIsVisible, isVisible }}
      />
    </InfoBlock>
  );
};
